/**
 * DocsBot AI integration utilities.
 */

declare global {
  interface Window {
    DocsBotAI: {
      init: (config: { id: string }) => Promise<void>;
      mount: (config: { id: string }) => Promise<void>;
      open: () => void;
      close: () => void;
      toggle: () => void;
      addUserMessage: (message: string, autoSend?: boolean) => Promise<void>;
    };
  }
}

const isDocsBotReady = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const api = window.DocsBotAI;
  return !!(api && typeof api.open === 'function' && typeof api.addUserMessage === 'function');
};

export const waitForDocsBotReady = async (
  timeoutMs: number = 3000,
  pollIntervalMs: number = 100
): Promise<boolean> => {
  if (isDocsBotReady()) {
    return true;
  }

  const start = Date.now();

  return new Promise((resolve) => {
    const interval = window.setInterval(() => {
      if (isDocsBotReady()) {
        window.clearInterval(interval);
        resolve(true);
      } else if (Date.now() - start >= timeoutMs) {
        window.clearInterval(interval);
        resolve(false);
      }
    }, pollIntervalMs);
  });
};

export const openDocsBot = async (): Promise<void> => {
  const ready = isDocsBotReady() || (await waitForDocsBotReady());
  if (!ready) {
    console.warn('DocsBot is not loaded yet. Please try again in a moment.');
    return;
  }

  try {
    window.DocsBotAI.open();
  } catch (error) {
    console.error('Error opening DocsBot:', error);
  }
};

export const askDocsBot = async (message: string, autoSend: boolean = true): Promise<void> => {
  const ready = isDocsBotReady() || (await waitForDocsBotReady());
  if (!ready) {
    console.warn('DocsBot is not loaded yet. Please try again in a moment.');
    return;
  }

  try {
    try {
      window.DocsBotAI.open();
    } catch {}

    await window.DocsBotAI.addUserMessage(message, autoSend).catch((error) => {
      console.error('Error sending message to DocsBot:', error);
    });
  } catch (error) {
    console.error('Error asking DocsBot:', error);
  }
};
